import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { AuthContext } from '../auth/AuthContext';
import {
  DEFAULT_DEPARTMENT,
  getDefaultTeamForRole,
  getJobRolesForDepartment,
  getTeamsForDepartment,
  resolveUserTeam,
  showsTeamPicker,
} from '../constants/departments';
import { useEnabledDepartments } from '../hooks/useEnabledDepartments';

const Onboarding = () => {
  const { refreshSession, user, needsOnboarding, sessionReady } = useContext(AuthContext);
  const { selectableDepartments } = useEnabledDepartments();
  const navigate = useNavigate();
  const [department, setDepartment] = useState(DEFAULT_DEPARTMENT);
  const [jobRole, setJobRole] = useState('developer');
  const [team, setTeam] = useState('Frontend');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);

  const jobRoles = useMemo(() => getJobRolesForDepartment(department), [department]);
  const teams = useMemo(() => getTeamsForDepartment(department), [department]);
  const showTeamPicker = showsTeamPicker(department);

  useEffect(() => {
    if (!selectableDepartments.length) return;
    if (!selectableDepartments.includes(department)) {
      setDepartment(selectableDepartments[0]);
    }
  }, [selectableDepartments, department]);

  useEffect(() => {
    if (!sessionReady) return;
    if (!needsOnboarding) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate, needsOnboarding, sessionReady]);

  useEffect(() => {
    if (!jobRoles.some((r) => r.value === jobRole)) {
      const next = jobRoles[0]?.value || 'developer';
      setJobRole(next);
      setTeam(getDefaultTeamForRole(next, department));
      return;
    }
    if (!teams.includes(team)) {
      setTeam(getDefaultTeamForRole(jobRole, department) || teams[0] || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when department or role list changes
  }, [department, jobRoles, teams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/complete-onboarding', {
        job_role: jobRole,
        team: resolveUserTeam(department, jobRole, team),
        department,
        full_name: fullName.trim() || undefined,
      });
      refreshSession(data.token, data.role, {
        id: data.userId,
        email: data.email,
        role: data.role,
        full_name: data.full_name,
        team: data.team,
        department: data.department || department,
        // Carried through deliberately: someone who signed up themselves is
        // still 'pending' at this point, and their department — just chosen
        // above — is what decides which managers approve them.
        status: data.status,
        needsOnboarding: false,
      });

      const awaitingApproval = data.status && data.status !== 'approved';
      toast.success(
        awaitingApproval
          ? 'Profile saved — your manager has been notified.'
          : data.message || 'Welcome!'
      );
      navigate(awaitingApproval ? '/pending-approval' : '/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save profile');
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      background: 'linear-gradient(135deg, #1272B3 0%, #2C8FC7 100%)',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          width: '100%',
          maxWidth: 440,
          background: 'rgba(255,255,255,0.98)',
          borderRadius: 20,
          padding: '2rem 2.25rem',
          boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
        }}
      >
        <h1 style={{ margin: '0 0 0.35rem', fontSize: '1.35rem', fontWeight: 800, color: '#0f172a' }}>
          Complete your profile
        </h1>
        <p style={{ margin: '0 0 1.5rem', fontSize: '0.88rem', color: '#64748b' }}>
          Signed in as <strong>{user?.email}</strong>. Choose your department and role{showTeamPicker ? ', and team' : ''}.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', color: '#2C63AE', marginBottom: 6 }}>
              Department
            </label>
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8,
                fontSize: '0.9rem', background: '#f8fafc',
              }}
            >
              {selectableDepartments.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', color: '#2C63AE', marginBottom: 6 }}>
              Job role
            </label>
            <select
              value={jobRole}
              onChange={(e) => {
                const next = e.target.value;
                setJobRole(next);
                setTeam(getDefaultTeamForRole(next, department));
              }}
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8,
                fontSize: '0.9rem', background: '#f8fafc',
              }}
            >
              {jobRoles.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {!showTeamPicker && (
              <p style={{ fontSize: '0.72rem', color: '#94a3b8', margin: '4px 0 0' }}>
                Work group is set automatically from role.
              </p>
            )}
          </div>

          {showTeamPicker && (
            <div>
              <label style={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', color: '#2C63AE', marginBottom: 6 }}>
                Team
              </label>
              <select
                value={team}
                onChange={(e) => setTeam(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8,
                  fontSize: '0.9rem', background: '#f8fafc',
                }}
              >
                {teams.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label style={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', color: '#2C63AE', marginBottom: 6 }}>
              Full name (optional)
            </label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your name"
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8,
                fontSize: '0.9rem', background: '#f8fafc', boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: 4, padding: '12px', background: '#1272B3', color: '#fff',
              border: 'none', borderRadius: 10, fontWeight: 800, fontSize: '1rem', cursor: 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Saving…' : 'Continue to Dashboard →'}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default Onboarding;
